<script lang="ts">
  // IMPORTANT: Svelte 5에서는 수동 레이아웃 래핑이 필요합니다
  // Inertia.js의 자동 레이아웃 할당이 Svelte 5와 호환되지 않음
  import { router } from '@inertiajs/svelte';

  interface ThreadsPost {
    id: string;
    threadsId: string;
    content: string;
    contentPreview: string;
    authorUsername: string;
    authorDisplayName: string | null;
    authorAvatarUrl: string | null;
    likesCount: number;
    repliesCount: number;
    repostsCount: number;
    quotesCount: number;
    postedAt: string;
    mediaUrls: string[];
    mediaType: string | null;
    fetchStatus: string;
    threadsUrl: string;
    hasMedia: boolean;
    postedAgo: string;
    statusBadge: {
      text: string;
      color: string;
      icon: string;
    };
    engagementSummary: {
      total: number;
      likes: number;
      replies: number;
      reposts: number;
      quotes: number;
    };
  }

  interface Stats {
    total: number;
    today: number;
    thisWeek: number;
    fetched: number;
    failed: number;
    pending: number;
    accountsCount: number;
    accountsEnabled: number;
  }

  interface Pagination {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    perPage: number;
    prevPage: number | null;
    nextPage: number | null;
  }

  let {
    posts = [],
    pagination = {},
    filters = {},
    stats = {}
  } = $props<{
    posts: ThreadsPost[];
    pagination: Pagination;
    filters: Record<string, string>;
    stats: Stats;
  }>();

  // Filter state
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let filterUsername = $state(filters.username || '');
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let filterStatus = $state(filters.status || '');
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let filterSearch = $state(filters.search || '');
  // svelte-ignore state_referenced_locally -- intentional: form filter initialized from prop
  let filterHasMedia = $state(filters.has_media || '');

  function applyFilters() {
    const params: Record<string, string> = {};
    if (filterUsername) params.username = filterUsername;
    if (filterStatus) params.status = filterStatus;
    if (filterSearch) params.search = filterSearch;
    if (filterHasMedia) params.has_media = filterHasMedia;

    router.get('/admin/threads_posts', params);
  }

  function clearFilters() {
    filterUsername = '';
    filterStatus = '';
    filterSearch = '';
    filterHasMedia = '';
    router.get('/admin/threads_posts');
  }

  function fetchNow() {
    const username = filterUsername || '';
    router.post('/admin/threads_posts/fetch_now', {
      username: username
    });
  }

  function goToPage(page: number) {
    const params: Record<string, any> = { page };
    if (filterUsername) params.username = filterUsername;
    if (filterStatus) params.status = filterStatus;
    if (filterSearch) params.search = filterSearch;
    if (filterHasMedia) params.has_media = filterHasMedia;

    router.get('/admin/threads_posts', params);
  }

  function getBadgeClass(color: string): string {
    const classes: Record<string, string> = {
      success: 'badge-success',
      error: 'badge-error',
      warning: 'badge-warning',
      default: 'badge-neutral'
    };
    return classes[color] || 'badge-neutral';
  }
</script>

<div class="container mx-auto px-4 py-8">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-content mb-2">
        Threads 게시글
      </h1>
      <p class="text-base text-content-secondary">
        크롤링된 Threads 게시글 목록 및 관리
      </p>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div class="card bg-base-100 shadow-sm border border-base-300 p-4">
        <div class="text-sm text-content-secondary mb-1">전체 게시글</div>
        <div class="text-2xl font-bold text-content">{stats.total}</div>
      </div>
      <div class="card bg-base-100 shadow-sm border border-base-300 p-4">
        <div class="text-sm text-content-secondary mb-1">오늘 수집</div>
        <div class="text-2xl font-bold text-primary">{stats.today}</div>
      </div>
      <div class="card bg-base-100 shadow-sm border border-base-300 p-4">
        <div class="text-sm text-content-secondary mb-1">성공</div>
        <div class="text-2xl font-bold text-success">{stats.fetched}</div>
      </div>
      <div class="card bg-base-100 shadow-sm border border-base-300 p-4">
        <div class="text-sm text-content-secondary mb-1">실패</div>
        <div class="text-2xl font-bold text-error">{stats.failed}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card bg-base-100 shadow-sm border border-base-300 p-4 mb-6">
      <div class="flex flex-wrap gap-4 items-end">
        <div class="flex-1 min-w-[200px]">
          <label for="filter-username" class="block text-sm font-medium mb-1">사용자명</label>
          <input
            id="filter-username"
            type="text"
            bind:value={filterUsername}
            placeholder="@username"
            class="input input-bordered w-full"
          />
        </div>
        <div class="flex-1 min-w-[150px]">
          <label for="filter-status" class="block text-sm font-medium mb-1">상태</label>
          <select id="filter-status" bind:value={filterStatus} class="select select-bordered w-full">
            <option value="">전체</option>
            <option value="fetched">성공</option>
            <option value="failed">실패</option>
            <option value="pending">대기</option>
          </select>
        </div>
        <div class="flex-1 min-w-[200px]">
          <label for="filter-search" class="block text-sm font-medium mb-1">검색</label>
          <input
            id="filter-search"
            type="text"
            bind:value={filterSearch}
            placeholder="게시글 내용 검색..."
            class="input input-bordered w-full"
          />
        </div>
        <div class="flex gap-2">
          <button
            onclick={applyFilters}
            class="btn btn-primary"
          >
            필터 적용
          </button>
          <button
            onclick={clearFilters}
            class="btn btn-ghost"
          >
            초기화
          </button>
          <button
            onclick={fetchNow}
            class="btn btn-secondary"
          >
            지금 가져오기
          </button>
        </div>
      </div>
    </div>

    <!-- Posts Table -->
    <div class="card bg-base-100 shadow-sm border border-base-300 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="table table-zebra w-full">
          <thead>
            <tr>
              <th>작성자</th>
              <th>내용</th>
              <th>참여도</th>
              <th>작성일</th>
              <th>상태</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {#each posts as post}
              <tr class="hover:bg-base-200">
                <td>
                  <div class="flex items-center gap-2">
                    {#if post.author_avatar_url}
                      <img
                        src={post.author_avatar_url}
                        alt={post.author_username}
                        class="w-10 h-10 rounded-full"
                      />
                    {/if}
                    <div>
                      <div class="font-medium">@{post.author_username}</div>
                      {#if post.author_display_name}
                        <div class="text-sm text-gray-500">{post.author_display_name}</div>
                      {/if}
                    </div>
                  </div>
                </td>
                <td>
                  <div class="max-w-md">
                    <p class="line-clamp-2 text-sm">{post.content_preview}</p>
                    {#if post.has_media}
                      <span class="badge badge-sm badge-info mt-1">미디어 {post.media_urls.length}개</span>
                    {/if}
                  </div>
                </td>
                <td>
                  <div class="text-sm">
                    <div>❤️ {post.likes_count}</div>
                    <div>💬 {post.replies_count}</div>
                    <div>🔁 {post.reposts_count}</div>
                  </div>
                </td>
                <td>
                  <div class="text-sm">
                    <div>{post.posted_ago}</div>
                    <div class="text-xs text-gray-500">
                      {new Date(post.posted_at).toLocaleDateString('ko-KR')}
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge {getBadgeClass(post.status_badge.color)}">
                    {post.status_badge.text}
                  </span>
                </td>
                <td>
                  <div class="flex gap-2">
                    <a
                      href={`/admin/threads_posts/${post.id}`}
                      class="btn btn-sm btn-ghost"
                    >
                      상세
                    </a>
                    <a
                      href={post.threads_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-sm btn-ghost"
                    >
                      Threads
                    </a>
                  </div>
                </td>
              </tr>
            {:else}
              <tr>
                <td colspan="6" class="text-center py-8 text-gray-500">
                  게시글이 없습니다.
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    {#if pagination.total_pages > 1}
      <div class="flex justify-center gap-2 mt-6">
        {#if pagination.prev_page}
          <button
            onclick={() => goToPage(pagination.prev_page!)}
            class="btn btn-sm"
          >
            이전
          </button>
        {/if}

        <span class="flex items-center px-4">
          {pagination.current_page} / {pagination.total_pages}
          <span class="text-sm text-gray-500 ml-2">
            (총 {pagination.total_count}개)
          </span>
        </span>

        {#if pagination.next_page}
          <button
            onclick={() => goToPage(pagination.next_page!)}
            class="btn btn-sm"
          >
            다음
          </button>
        {/if}
      </div>
    {/if}
  </div>
